/**
 * DTO typy sdílené mezi workerem a frontendem.
 * Worker serializuje integer 0/1 na boolean a unix sekundy nechává jako number.
 */

export type Role = 'admin' | 'user';

export interface MeDto {
  id: string;
  email: string;
  name: string;
  pictureUrl: string | null;
  role: Role;
}

/** Odpověď GET /api/me (a /api/auth/google/callback). */
export interface MeResponse {
  user: MeDto;
}

export type BrandId = 'buydubai' | 'willroi';

export interface BrandDto {
  id: BrandId;
  name: string;
  color: string;
  logoUrl: string;
  emailFrom: string;
  replyTo: string;
  crmTitlePrefix: string;
  crmStatusId: number;
  crmMktChannel: number;
  crmCreatedBy: string;
  crmRegion: number;
  /** Region podle jazyka formuláře (`cs`, `sk`…); jazyk bez záznamu → crmRegion. */
  crmRegionByLocale: Record<string, number>;
  crmChannelLabel: string;
  confirmSubject: string;
  confirmHtml: string;
  brokerNotifySubject: string;
  brokerNotifyHtml: string;
  whatchimpBrokerTemplate: string;
  whatchimpLeadTemplate: string;
}

export interface MetaPageDto {
  id: string;
  name: string;
  brandId: BrandId | null;
  hasToken: boolean;
  subscribed: boolean;
  lastCheckedAt: number | null;
}

export interface LeadFormDto {
  id: string;
  pageId: string;
  name: string;
  status: string;
  brandId: BrandId | null;
  active: boolean;
  emailEnabled: boolean;
  confirmSubject: string | null;
  confirmHtml: string | null;
  locale: string;
  brokerEmailEnabled: boolean;
  whatchimpBrokerEnabled: boolean;
  whatchimpLeadEnabled: boolean;
  brokerEmails: string[];
  brokerPhones: string[];
  /** false = leady formuláře se do CRM neposílají. */
  crmEnabled: boolean;
  eventsEnabled: boolean;
  /** Plná URL endpointu akce v buydubai-events. */
  eventsEndpoint: string;
  /** Token se z API nikdy nevrací — jen příznak, že je uložený. */
  hasEventsToken: boolean;
  eventsSendConfirmation: boolean;
  eventsMarketingConsent: boolean;
  /**
   * Pravidla „odpověď ve formuláři → akce“. Vyhodnocují se shora, vyhrává první shoda;
   * když nějaká jsou a žádné nesedí, lead se do Events nepošle. Prázdné = výchozí endpoint.
   */
  eventsRoutes: EventRouteDto[];
  notes: string;
}

export type EventRouteMatch = 'contains' | 'equals';

export interface EventRouteDto {
  id: string;
  label: string;
  /** Klíč pole z odpovědí formuláře. */
  field: string;
  match: EventRouteMatch;
  value: string;
  endpoint: string;
  /** Token se nevrací, jen příznak. */
  hasToken: boolean;
  sendConfirmation: boolean;
  marketingConsent: boolean;
}

/**
 * Pravidlo v PUT /api/meta/forms/:id. `token` chybí = ponechat uložený (podle id),
 * prázdný řetězec = smazat, jinak se zašifruje a uloží.
 */
export type EventRouteUpdate = Omit<EventRouteDto, 'hasToken'> & { token?: string };

/** GET /api/meta/forms/:id/fields — pole a odpovědi z posledních leadů (nápověda pro pravidla). */
export interface FormFieldValuesDto {
  fields: { key: string; values: { value: string; count: number }[] }[];
}

/**
 * Tělo PUT /api/meta/forms/:id. Token akce se nikdy nečte zpět, takže v DTO není —
 * zapisuje se jen tudy (prázdný řetězec uložený token smaže).
 */
export type LeadFormUpdate = Partial<Omit<LeadFormDto, 'hasEventsToken' | 'eventsRoutes'>> & {
  eventsToken?: string;
  eventsRoutes?: EventRouteUpdate[];
};

export type CrmStatus = 'pending' | 'ok' | 'error' | 'failed' | 'skipped';
/**
 * 'import' = zpětný import bez automatického doručení — do CRM jen ručně.
 * 'disabled' = formulář má odesílání do CRM vypnuté.
 */
export type CrmSkipReason = 'no-phone' | 'unmapped' | 'import' | 'disabled';

/** Doručení do aplikace pro eventy má stejné stavy jako CRM. */
export type EventStatus = CrmStatus;
/**
 * 'unconfigured' = formulář (nebo vybrané pravidlo) nemá endpoint či token akce.
 * 'no-route' = formulář má pravidla podle odpovědi, ale žádné nesedí.
 */
export type EventSkipReason = 'no-email' | 'unconfigured' | 'import' | 'disabled' | 'no-route';
export type NotificationChannel =
  | 'email_lead'
  | 'email_broker'
  | 'whatchimp_broker'
  | 'whatchimp_lead'
  /** Ruční přeposlání vybraných leadů e-mailem ze seznamu. */
  | 'email_forward'
  /** Vlastní e-mail leadovi napsaný v hromadné akci ze seznamu. */
  | 'email_custom';
export type NotificationStatus = 'ok' | 'error' | 'skipped';

/** Odpověď POST /api/leads/bulk/resend — stav doručení každého leadu po pokusu. */
export interface BulkResendResultDto {
  results: {
    id: string;
    status: CrmStatus;
    skipReason: CrmSkipReason | EventSkipReason | null;
    /** Lead už byl doručený a přeskočil se (includeDelivered: false). */
    alreadyDelivered: boolean;
  }[];
}

export interface LeadListItemDto {
  id: string;
  createdAt: number;
  /** created_time z Mety (unix sekundy) — kdy lead vyplnil formulář. */
  metaCreatedTime: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  brandId: BrandId | null;
  formId: string;
  formName: string;
  crmStatus: CrmStatus;
  crmSkipReason: CrmSkipReason | null;
  crmAttempts: number;
  /** NULL = lead vznikl před zapojením Events, doručení se nikdy nezakládalo. */
  eventStatus: EventStatus | null;
  eventSkipReason: EventSkipReason | null;
  emailLeadStatus: NotificationStatus | null;
}

export interface NotificationDto {
  id: string;
  channel: NotificationChannel;
  recipient: string;
  subject: string;
  status: NotificationStatus;
  detail: string;
  manual: boolean;
  createdAt: number;
}

export interface LeadDetailDto extends LeadListItemDto {
  pageId: string;
  adId: string | null;
  campaignName: string | null;
  fields: Record<string, string>;
  source: 'webhook' | 'backfill';
  crm: {
    status: CrmStatus;
    skipReason: CrmSkipReason | null;
    attempts: number;
    lastAttemptAt: number | null;
    nextAttemptAt: number | null;
    responseStatus: number | null;
    responseJson: string | null;
    detail: string;
    followupsAt: number | null;
  } | null;
  event: {
    status: EventStatus;
    skipReason: EventSkipReason | null;
    attempts: number;
    lastAttemptAt: number | null;
    nextAttemptAt: number | null;
    responseStatus: number | null;
    responseJson: string | null;
    registrationId: string | null;
    /** Events vrátil duplicate:true — účastník už na akci byl. */
    duplicate: boolean;
    /** Název akce z pravidla ('' = výchozí endpoint formuláře). */
    routeLabel: string;
    detail: string;
  } | null;
  notifications: NotificationDto[];
}

export interface UserDto {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: boolean;
  createdAt: number;
  lastLoginAt: number | null;
}

export interface WebhookStatusDto {
  appSecretSet: boolean;
  verifyTokenSet: boolean;
  globalTokenSet: boolean;
  crmTokenSet: boolean;
  resendKeySet: boolean;
  whatchimpKeySet: boolean;
  callbackUrl: string;
  lastWebhook: { receivedAt: number; outcome: string; entries: number; detail: string } | null;
  rejected7d: number;
}

/**
 * Placeholdery šablon — `{jmeno}` apod. Neznámé klíče zůstávají v textu literálně.
 * Používají se v e-mailech (HTML) i Whatchimp zprávách (plain text).
 */
export const TEMPLATE_PLACEHOLDERS = [
  'jmeno',
  'email',
  'telefon',
  'formular',
  'znacka',
  'kampan',
] as const;
export type TemplatePlaceholder = (typeof TEMPLATE_PLACEHOLDERS)[number];
